import _ from 'lodash';

import { CONFIGURATION_FEATURES_NAMES } from '../../Types';
import { isFeatureEnabled } from '../../Utils';
import useOrganization from '../OrganizationContext';
import type { ClaimPolymorph } from '../types/claim-types';
import useDataFetcher from '../useDataFetcher';

import { useCms } from './useCms';

// Based on server closing reason structure and API responses
interface LegacyClosingReason {
  desc: string;
  extra_needed?: boolean;
  internal_only?: boolean;
  coverage_specific?: string[];
}

interface NewClosingReason {
  desc: string;
  extra_needed: boolean;
  is_note_required: boolean;
}

type ClosingReasonsDict = Record<string, LegacyClosingReason | NewClosingReason>;

interface ExposuresClosingReasonsDict {
  [lob: string]: Record<string, LegacyClosingReason>;
}

interface OrganizationContextType {
  exposuresClosingReasonsDict: ExposuresClosingReasonsDict;
}

// Based on API response from /api/v1/claims/${claim.id}/exposures/closing_reasons
interface ApiClosingReason {
  closing_reason_key: string;
  display_name: string;
  is_note_enabled: boolean;
  is_note_required: boolean;
}

interface UseExposureClosingReasonsReturn {
  possibleReasons: string[];
  exposuresClosingReasons: ClosingReasonsDict;
  isClosingReasonsLoaded: boolean;
  isNewClosingReason: boolean;
}

function useExposureClosingReasons(
  claim: ClaimPolymorph,
  exposures: ClaimPolymorph['exposures'] = []
): UseExposureClosingReasonsReturn {
  const { exposuresClosingReasonsDict } = useOrganization() as OrganizationContextType;
  const { userOrganization } = useCms();
  const isNewClosingReason = isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.NEW_CLOSING_REASON);

  const { data: closingReasons, isLoading: isClosingReasonsLoading } = useDataFetcher<ApiClosingReason[]>(
    isNewClosingReason ? `/api/v1/claims/${claim.id}/exposures/closing_reasons` : null
  );

  // Calculate possible reasons and exposures closing reasons
  const coverages: string[] = exposures.map((exposure) => exposure.coverage_type);
  const lobKey: string = typeof claim?.lob === 'string' ? claim.lob : '';
  const defaultClosingReasons: ClosingReasonsDict = {};
  let exposuresClosingReasons: ClosingReasonsDict = exposuresClosingReasonsDict[lobKey] ?? defaultClosingReasons;
  let possibleReasons: string[] = Object.keys(exposuresClosingReasons)
    .filter((reason) => {
      const reasonData = exposuresClosingReasons[reason] as LegacyClosingReason;
      return !reasonData.internal_only;
    })
    .filter((reason) => {
      const reasonData = exposuresClosingReasons[reason] as LegacyClosingReason;
      return !(reasonData.coverage_specific && _.intersection(reasonData.coverage_specific, coverages).length === 0);
    });

  if (isNewClosingReason) {
    const reasonDict: Record<string, NewClosingReason> = {};
    closingReasons?.forEach((closingReason) => {
      reasonDict[closingReason.closing_reason_key] = {
        desc: closingReason.display_name,
        extra_needed: closingReason.is_note_enabled,
        is_note_required: closingReason.is_note_required,
      };
    });
    possibleReasons = Object.keys(reasonDict);
    exposuresClosingReasons = reasonDict;
  }

  return {
    possibleReasons,
    exposuresClosingReasons,
    isClosingReasonsLoaded: isNewClosingReason ? !isClosingReasonsLoading : true,
    isNewClosingReason,
  };
}

export default useExposureClosingReasons;
